import { type FC, useMemo, useState } from "react";

import moment from "moment";

import {
  Button,
  Icon,
  Table,
  type TableColumn,
  Text,
  toastError,
  toastSuccess,
  useModal,
} from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import type { UseGetAllRelationshipsReturn } from "~/v2/api/hooks/useGetAllRelationships";
import type { UseGetAllTransfersReturn } from "~/v2/api/hooks/useGetAllTransfers";
import type { Account } from "~/v2/api/rest/account";
import { deleteAchTransfer, type Transfer } from "~/v2/api/rest/banking";
import { getPrettyCash, getPrettyDate } from "~/v2/utils/formatting";
import { TransferDirection } from "../common/const";

const STATUS_MAP = {
  COMPLETE: "Complete",
  PENDING: "Pending",
  QUEUED: "Pending",
  EXECUTED: "Pending",
  REJECTED: "Rejected",
  CANCELED: "Canceled",
  APPROVED: "Pending",
  APPROVAL_PENDING: "Pending",
  FUNDS_POSTED: "Pending",
  SENT_TO_CLEARING: "Pending",
  SENT_TO_BANK: "Pending",
  RETURNED: "Returned",
  PENDING_PRINTING: "Pending",
  VOID: "Invalid",
  STOP_PAYMENT: "Stop Payment",
  EXPIRED: "Expired",
};

const CANCELABLE_STATUSES = ["PENDING", "QUEUED", "APPROVAL_PENDING"];

type TransferWithRelationshipId = Transfer & {
  relationship_id?: string;
  bank_id?: string;
};

type TransferHistoryTableProps = {
  account: Account;
  relationships: UseGetAllRelationshipsReturn;
  transfers: UseGetAllTransfersReturn;
};

export const TransferHistoryTable: FC<TransferHistoryTableProps> = ({
  account,
  transfers: { achAndWire, rapyd, currencyCloud },
  relationships,
}) => {
  const [selectedPendingTransfer, setSelectedPendingTransfer] =
    useState<Transfer | null>(null);

  // Cast as TransferWithRelationshipId, they might have them
  const rows = useMemo<TransferWithRelationshipId[]>(
    () =>
      [...achAndWire.transfers, ...rapyd.transfers, ...currencyCloud.transfers]
        // sort in descending order
        .sort((a, b) => moment(b.created_at).diff(moment(a.created_at))),
    [achAndWire.transfers, rapyd.transfers, currencyCloud.transfers],
  );

  const achRelationship = relationships.ach.relationships.at(0);
  const wireRelationship = relationships.wire.relationships.at(0);
  const ccAccount = relationships.currencyCloud.account;

  const renderToFrom = (
    transfer: TransferWithRelationshipId,
    column: "FROM" | "TO",
  ) => {
    const direction = transfer.direction?.toUpperCase() ?? "";

    if (
      (column === "FROM" && direction !== TransferDirection.INCOMING) ||
      (column === "TO" && direction === TransferDirection.INCOMING)
    ) {
      return (
        <Text className="text-sm">
          Alpaca Account
          <br />({account.account_number})
        </Text>
      );
    }

    const type = transfer.type?.toUpperCase() ?? "";

    if (
      (type === "ACH" || type === "INSTANT_ACH" || type === "RAPYD") &&
      achRelationship &&
      achRelationship.id === transfer?.relationship_id
    ) {
      return (
        <Text className="text-sm">
          {achRelationship.brand_name} <br />({achRelationship.account_name})
        </Text>
      );
    }

    if (
      type === "WIRE" &&
      wireRelationship &&
      wireRelationship.id === transfer?.bank_id
    ) {
      return (
        <Text className="text-sm">
          {wireRelationship.name} <br />({wireRelationship.account_number})
        </Text>
      );
    }

    if (type === "CURRENCY_CLOUD" && ccAccount) {
      return (
        <Text className="text-sm">
          {ccAccount.bank_name} <br />({ccAccount.account_number})
        </Text>
      );
    }

    return <Text className="text-sm">-</Text>;
  };

  const renderAmount = (transfer: Transfer) => {
    const { amount } = transfer;
    const direction = transfer.direction?.toUpperCase() ?? "";

    // normalize the amount
    const value = Number(amount).toLocaleString("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });

    const sign = direction === TransferDirection.INCOMING ? "+" : "-";

    return (
      <Text className="text-sm">
        {sign}
        {getPrettyCash(value)}
      </Text>
    );
  };

  const renderStatus = (transfer: TransferWithRelationshipId) => {
    const direction =
      (transfer.direction?.toUpperCase() ?? "") === TransferDirection.INCOMING
        ? "Deposit"
        : "Withdrawal";

    const type = transfer.type?.toUpperCase() ?? "";
    const status = transfer.status?.toUpperCase() ?? "";
    const displayStatus =
      STATUS_MAP[status as keyof typeof STATUS_MAP] ?? transfer.status ?? "-";

    // Type must be ACH or WIRE and relationship must exist
    const isTypeCancelable =
      (type === "ACH" &&
        !!achRelationship &&
        achRelationship.id === transfer.relationship_id) ||
      (type === "WIRE" &&
        !!wireRelationship &&
        wireRelationship.id === transfer.bank_id);

    const isStatusCancelable = CANCELABLE_STATUSES.includes(status);
    const isTransferCancelable = isTypeCancelable && isStatusCancelable;

    if (isTransferCancelable) {
      return (
        <button
          type="button"
          onClick={() => onPendingTransferClicked(transfer)}
          className="flex items-center justify-between text-sm hover:underline"
        >
          <span>
            {direction} {displayStatus}
          </span>{" "}
          <Icon name="XMark" className="ml-2" />
        </button>
      );
    }

    return (
      <Text className="text-sm">
        {direction} {displayStatus}
      </Text>
    );
  };

  const cancelMutation = useMutationRequest(
    (transferId: string) => deleteAchTransfer(account.id, transferId),
    {
      onSuccess: () => {
        setSelectedPendingTransfer(null);
        cancelModal.toggle();
        toastSuccess("Transfer canceled");
        achAndWire.refetch();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(
          `An error occurred cancelling pending transfer: ${errorMessage}`,
        );
      },
    },
  );

  const cancelModal = useModal({
    title: `Cancel Transfer`,
    content: selectedPendingTransfer ? (
      <div>
        <Text className="pb-8">
          Are you sure you want to cancel this transfer?
        </Text>

        <div className="flex gap-4 pb-4">
          <Text className="min-w-20">From:</Text>{" "}
          {renderToFrom(selectedPendingTransfer, "FROM")}
        </div>
        <div className="flex gap-4 pb-4">
          <Text className="min-w-20">To:</Text>{" "}
          {renderToFrom(selectedPendingTransfer, "TO")}
        </div>
        <div className="flex gap-4">
          <Text className="min-w-20">Amount:</Text>{" "}
          {renderAmount(selectedPendingTransfer)}
        </div>
      </div>
    ) : null,
    controls: selectedPendingTransfer ? (
      <div className="flex w-full items-center justify-between">
        <Button variant="alternate" onClick={() => cancelModal.toggle()}>
          Cancel
        </Button>
        <Button
          variant="red"
          disabled={cancelMutation.isPending}
          onClick={() => cancelMutation.mutate(selectedPendingTransfer.id)}
        >
          Yes, Cancel Transfer
        </Button>
      </div>
    ) : null,
  });

  const onPendingTransferClicked = (transfer: Transfer) => {
    setSelectedPendingTransfer(transfer);
    cancelModal.toggle();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: rows is listed to refresh the cell renderers' closures: renderToFrom, renderStatus and renderAmount are redefined every render, so listing them would rebuild the columns every render, while dropping rows would freeze them at their first-render values. They cannot be memoized in place because renderStatus closes over onPendingTransferClicked and cancelModal, both declared below it, so useCallback deps would be a TDZ error. Reordering those declarations is the real fix and is left for a follow-up.
  const columns = useMemo<TableColumn<Transfer>[]>(
    () => [
      {
        name: "Date",
        key: "created_at",
        cellRenderer: ({ created_at }) => (
          <Text className="text-sm">
            {getPrettyDate(moment(created_at).toDate().getTime())}
          </Text>
        ),
      },
      {
        name: "From",
        key: "from",
        cellRenderer: (transfer) => renderToFrom(transfer, "FROM"),
      },
      {
        name: "To",
        key: "to",
        cellRenderer: (transfer) => renderToFrom(transfer, "TO"),
      },
      {
        name: "Type",
        key: "type",
        cellRenderer: ({ type }) => (
          <Text className="text-sm">{type?.toUpperCase() ?? ""}</Text>
        ),
      },
      {
        name: "Amount",
        key: "amount",
        alignRight: true,
        cellRenderer: renderAmount,
      },
      {
        name: "Status",
        key: "status",
        cellRenderer: renderStatus,
      },
    ],
    [rows],
  );

  return (
    <>
      <Table
        searchEnabled
        maxHeight={450}
        emptyMessage="No transfers found"
        uniqueKey="page:banking"
        columns={columns}
        rows={rows}
        pageSize={10}
        paginationEnabled={true}
      />
      {cancelModal.component}
    </>
  );
};
